/**
 * `gamification-core` — CONFIG of stockpilot domain (inventory).
 *
 * OPT-IN and NON-punitive retrofit (B2B): gamification rewards keeping
 * inventory MOVING and ACCURATE (recorded movements, confirmed
 * operations, reconciled counts, consistency) as ENGAGEMENT — never as
 * operator surveillance. Private leagues by default (§leagues). The engine is
 * agnostic; here we only declare the rules.
 *
 * Gamification is DERIVED from `activity` (immutable hash-chain) — see `derive.ts`.
 * It does not alter the log: it is a pure and recomputable projection. It only relies on
 * entities that the write-path ACTUALLY tracks (see `tracked_fields` in
 * core-rust): `stock_move` (append-only) and `stock_picking` (state). Cycle
 * count (`inventory_count`) is NOT tracked as an entity, but its validation
 * MATERIALIZES `stock_move`s with `reason = "count-adjustment"` — from there we derive
 * the count reconciliation event without touching the server.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Domain event types (derived from the `activity` feed). */
export const STOCKPILOT_EVENTS = {
  /** Confirmed operation (receipt/delivery/transfer): draft→done. */
  pickingConfirmed: "picking.confirmed",
  /** Cycle count discrepancy reconciled (move with reason count-adjustment). */
  countAdjusted: "count.adjusted",
  /** Recorded stock movement (any other append-only move). */
  stockMoved: "stock.moved",
} as const;

/** Streak line: days keeping inventory in motion (consistency). */
export const INVENTORY_STREAK_LINE = "inventory";

export const stockpilotGamificationConfig: GamificationConfig = {
  xp: {
    [STOCKPILOT_EVENTS.pickingConfirmed]: 25,
    [STOCKPILOT_EVENTS.countAdjusted]: 5,
    [STOCKPILOT_EVENTS.stockMoved]: 3,
  },
  streakLines: {
    // Any inventory movement (recorded or count) marks the day.
    [STOCKPILOT_EVENTS.stockMoved]: [INVENTORY_STREAK_LINE],
    [STOCKPILOT_EVENTS.countAdjusted]: [INVENTORY_STREAK_LINE],
  },
  counters: {
    [STOCKPILOT_EVENTS.pickingConfirmed]: ["pickings_confirmed"],
    [STOCKPILOT_EVENTS.countAdjusted]: ["counts_adjusted"],
    [STOCKPILOT_EVENTS.stockMoved]: ["stock_moves"],
  },
  badges: [
    {
      id: "first-move",
      title: "First move",
      description: "Register your first stock movement.",
      when: counterAtLeast("stock_moves", 1),
    },
    {
      id: "mover",
      title: "Mover",
      description: "Register fifty stock movements.",
      when: counterAtLeast("stock_moves", 50),
    },
    {
      id: "accurate",
      title: "Accurate",
      description: "Reconcile ten cycle-count differences.",
      when: counterAtLeast("counts_adjusted", 10),
    },
    {
      id: "steady-week",
      title: "Steady week",
      description: "Keep inventory moving seven days in a row.",
      when: streakAtLeast(INVENTORY_STREAK_LINE, 7),
      bonusXp: 50,
    },
  ],
  quests: [
    {
      id: "daily-moves",
      title: "Five stock moves today",
      period: "daily",
      goals: [{ metric: STOCKPILOT_EVENTS.stockMoved, target: 5 }],
      bonusXp: 20,
    },
  ],
};
